import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { GlobalSettings } from "../global_settings.js";
import { centerSliceObjectGeometry } from "../utils/center_slice_geometry.js";
import type { TableShape } from "../level_config.js";

export interface HangConfig
{
  anchorHeight: number;
  swingDeg: number;
  swingPeriodSec: number;
}

export interface ElevatorConfig
{
  riseHeight: number;
  periodSec: number;
}

export interface SlideConfig
{
  distance: number;
  periodSec: number;
}

export class PhysicsTable
{
  // Treat imported art as a compact 1x1 centre-slice template. Gameplay tables
  // are normally wider/deeper than this, so their flat middle grows while the
  // rounded rim stays small. A 5x5 template made the preserved corners consume
  // nearly the entire depth when fitted to blocks, producing a pill/oval shape.
  private static readonly BASE_TOP_SIZE = new THREE.Vector3( 1, 0.4064, 1 );
  // The authored support is visually too heavy next to the slim tabletop.
  // Keep its height, but reduce both horizontal axes so the post and foot
  // retain their original proportions.
  private static readonly LEG_XZ_SCALE = 0.72;
  private static readonly LEG_COVER_FRACTION = 0.7;
  private static readonly LEG_LEAN_DEG = 12;
  private static readonly MODEL_SCALE = 0.5;
  private static readonly MODEL_YAW = Math.PI * 0.5;
  private static min_top_size: THREE.Vector3 | null = null;

  private static readonly LEG_POST_TAG = "pillar";
  private static readonly MIN_LEG_POST_HEIGHT = 0.02;

  private readonly model: THREE.Object3D;
  private readonly table_top: THREE.Object3D;
  private readonly table_leg: THREE.Object3D;
  private readonly top_pivot: THREE.Group;

  private readonly tilt_pivot: THREE.Group;
  private table_leg_far: THREE.Object3D | null = null;
  private readonly body: RAPIER.RigidBody;
  private readonly top_body: RAPIER.RigidBody;
  private readonly shape: TableShape;

  private readonly rounded: boolean;
  private static readonly OVAL_COLLIDER_BANDS = 11;

  private readonly box = new THREE.Box3();
  private readonly size = new THREE.Vector3();
  private readonly center = new THREE.Vector3();
  private readonly top_center = new THREE.Vector3();
  private readonly top_size = new THREE.Vector3();
  private readonly leg_size = new THREE.Vector3();
  private platform_bottom_y = 0;

  private readonly pivot: THREE.Vector3;
  private rotation_angle = 0;
  private readonly unrotated_pos = new THREE.Vector3();

  private readonly tilt_rad: number;

  private lift: number;
  private readonly stand_y?: number;

  private static readonly tmp_scale = new THREE.Vector3();

  private readonly untilted_top_size = new THREE.Vector3();
  private readonly untilted_top_center = new THREE.Vector3();

  private readonly hang?: HangConfig;
  private swing_pivot?: THREE.Group;

  private hang_pivot_world?: THREE.Vector3;
  private cable_attach_left?: THREE.Object3D;
  private cable_attach_right?: THREE.Object3D;
  private static readonly tmp_hang_offset = new THREE.Vector3();
  private static readonly tmp_hang_center = new THREE.Vector3();
  private static readonly tmp_hang_quat = new THREE.Quaternion();

  private swing_angle = 0;
  private static readonly tmp_unswing = new THREE.Vector3();
  private static readonly tmp_unswing_quat = new THREE.Quaternion();

  private static readonly HANG_AXIS = new THREE.Vector3( 0, 0, 1 );

  private readonly elevator?: ElevatorConfig;
  private elevation = 0;
  private tilt_pivot_rest_y = 0;
  private top_body_rest_y = 0;
  private surface_rest: THREE.Vector3 | null = null;
  private leg_rest_height = 0;
  private leg_far_rest_height = 0;
  private readonly stretch_colliders: { collider: RAPIER.Collider; half: THREE.Vector3; at: THREE.Vector3 }[] = [];

  private readonly slide?: SlideConfig;
  private slide_offset = 0;

  private readonly slide_axis = new THREE.Vector3( 1, 0, 0 );
  private slide_units = 1;
  private readonly tilt_pivot_rest = new THREE.Vector3();
  private top_body_rest_x = 0;
  private static readonly tmp_slide_matrix = new THREE.Matrix4();

  public static getBaseTopSize(): THREE.Vector3
  {
    return PhysicsTable.BASE_TOP_SIZE.clone();
  }

  public static getMinTopSize(): THREE.Vector3
  {
    if ( !PhysicsTable.min_top_size )
    {
      const leg = ResourceManager.getModel( ResourceID.TableLegModel );
      PhysicsTable.shapeLeg( leg );

      const model = new THREE.Group();
      model.add( leg );
      model.scale.setScalar( PhysicsTable.MODEL_SCALE );
      model.rotation.y = PhysicsTable.MODEL_YAW;
      model.updateMatrixWorld( true );

      const leg_size = new THREE.Box3().setFromObject( leg ).getSize( new THREE.Vector3() );
      PhysicsTable.min_top_size = new THREE.Vector3( leg_size.x, 0, leg_size.z )
        .multiplyScalar( PhysicsTable.LEG_COVER_FRACTION );
    }

    return PhysicsTable.min_top_size.clone();
  }

  private static shapeLeg( leg: THREE.Object3D ): void
  {
    leg.scale.set(
      leg.scale.x * PhysicsTable.LEG_XZ_SCALE,
      leg.scale.y * 2 / 3,
      leg.scale.z * PhysicsTable.LEG_XZ_SCALE
    );
  }

  constructor(
    position: THREE.Vector3,
    target_depth?: number,
    target_width?: number,
    texture_id: ResourceID = ResourceID.TableBlueBaseMap,
    tilt_rad = 0,
    lift = 0,
    stand_y?: number,
    clearance = 0,
    hang?: HangConfig,
    shape: TableShape = "rectangular",
    elevator?: ElevatorConfig,
    slide?: SlideConfig
  )
  {
    this.shape = shape;
    this.rounded = shape !== "rectangular";
    this.pivot = position.clone();
    this.tilt_rad = tilt_rad;
    this.lift = lift;
    this.stand_y = stand_y;
    this.hang = hang;

    this.elevator = hang ? undefined : elevator;
    // A hanging table has no leg to stay behind, so there is nothing for the
    // deck to slide along.
    this.slide = hang ? undefined : slide;
    const parts = this.createModel( position, texture_id );
    this.model = parts.model;
    this.table_top = parts.table_top;
    this.table_leg = parts.table_leg;
    this.top_pivot = parts.top_pivot;
    this.tilt_pivot = parts.tilt_pivot;

    this.standOn( stand_y, clearance );

    if ( this.rounded )
      this.resizeRoundTableTop( target_depth, target_width );
    else
    {
      this.stretchTableTopDepth( target_depth );
      this.stretchTableTopWidth( target_width );
    }

    const flat_top_box = new THREE.Box3().setFromObject( this.table_top );
    flat_top_box.getSize( this.untilted_top_size );
    flat_top_box.getCenter( this.untilted_top_center );

    this.applyTilt();

    if ( this.hang )
      this.createHangRig( this.hang, flat_top_box );

    this.calculateBounds();

    this.body = this.createRigidBody( position );
    this.top_body = this.createTabletopBody( position );

    this.createTableTopCollider( position );

    if ( !this.hang )
      this.createLegCollider( position );

    if ( !this.hang && !this.elevator && this.stand_y === undefined )
    {
      this.leanLeg( this.table_leg );

      if ( this.table_leg_far )
        this.leanLeg( this.table_leg_far );
    }

    if ( this.elevator )
      this.captureElevatorRest();

    if ( this.slide )
      this.captureSlideRest();
  }

  private standOn( stand_y?: number, clearance = 0 ): void
  {
    if ( stand_y === undefined )
      return;

    this.model.updateMatrixWorld( true );

    const extra = stand_y + clearance - new THREE.Box3().setFromObject( this.table_top ).min.y;

    this.lift += extra;
    this.model.position.y += extra;
    this.model.updateMatrixWorld( true );
  }

  private createHangRig( hang: HangConfig, rest_top_box: THREE.Box3 ): void
  {
    this.model.remove( this.table_leg );

    const swing_pivot = new THREE.Group();
    this.model.add( swing_pivot );
    this.model.updateMatrixWorld( true );

    const rest_center = new THREE.Vector3();
    rest_top_box.getCenter( rest_center );
    const pivot_y = rest_top_box.max.y + hang.anchorHeight;
    const pivot_world = new THREE.Vector3( rest_center.x, pivot_y, rest_center.z );

    this.hang_pivot_world = pivot_world.clone();

    swing_pivot.position.copy( this.model.worldToLocal( pivot_world ) );

    swing_pivot.attach( this.tilt_pivot );
    this.swing_pivot = swing_pivot;

    this.model.updateMatrixWorld( true );

    this.cable_attach_left = this.createCableAttachment(
      new THREE.Vector3( rest_top_box.min.x, rest_top_box.max.y, rest_center.z )
    );
    this.cable_attach_right = this.createCableAttachment(
      new THREE.Vector3( rest_top_box.max.x, rest_top_box.max.y, rest_center.z )
    );
  }

  private createCableAttachment( rest_world: THREE.Vector3 ): THREE.Object3D
  {
    const marker = new THREE.Object3D();
    marker.position.copy( this.top_pivot.worldToLocal( rest_world.clone() ) );
    this.top_pivot.add( marker );
    return marker;
  }

  public getCableAttach( side: "left" | "right", out: THREE.Vector3 ): THREE.Vector3 | null
  {
    const marker = side === "left" ? this.cable_attach_left : this.cable_attach_right;

    return marker ? marker.getWorldPosition( out ) : null;
  }

  public getRestTopBox(): THREE.Box3
  {
    return new THREE.Box3().setFromCenterAndSize( this.untilted_top_center, this.untilted_top_size );
  }

  public setHangPivot( world: THREE.Vector3 ): void
  {
    if ( !this.swing_pivot || !this.hang_pivot_world )
      return;

    this.model.attach( this.tilt_pivot );
    this.model.updateMatrixWorld( true );

    this.swing_pivot.position.copy( this.model.worldToLocal( world.clone() ) );
    this.model.updateMatrixWorld( true );

    this.swing_pivot.attach( this.tilt_pivot );
    this.model.updateMatrixWorld( true );

    this.hang_pivot_world.copy( world );
  }

  public getSwingPivotWorld(): THREE.Vector3 | undefined
  {
    return this.hang_pivot_world;
  }

  public isHanging(): boolean
  {
    return !!this.hang;
  }

  public setSwingAngle( rad: number ): void
  {
    if ( !this.swing_pivot || !this.hang_pivot_world )
      return;

    this.swing_angle = rad;
    this.swing_pivot.rotation.x = rad;
    this.model.updateMatrixWorld( true );

    const q = PhysicsTable.tmp_hang_quat.setFromAxisAngle( PhysicsTable.HANG_AXIS, -rad );

    const offset = PhysicsTable.tmp_hang_offset
      .copy( this.untilted_top_center )
      .sub( this.hang_pivot_world )
      .applyQuaternion( q );
    const new_center = PhysicsTable.tmp_hang_center
      .copy( this.hang_pivot_world )
      .add( offset );

    this.top_body.setTranslation( { x: new_center.x, y: new_center.y, z: new_center.z }, false );
    this.top_body.setRotation( { x: q.x, y: q.y, z: q.z, w: q.w }, false );
  }

  public isElevating(): boolean
  {
    return !!this.elevator;
  }

  public getRiseHeight(): number
  {
    return this.elevator?.riseHeight ?? 0;
  }

  public getElevation(): number
  {
    return this.elevation;
  }

  public setElevation( dy: number ): void
  {
    if ( !this.elevator )
      return;

    this.elevation = dy;

    const world_scale_y = this.model.getWorldScale( PhysicsTable.tmp_scale ).y || 1;

    this.tilt_pivot.position.y = this.tilt_pivot_rest_y + dy / world_scale_y;
    this.model.updateMatrixWorld( true );

    this.stretchLegTo( this.table_leg, this.leg_rest_height + dy );

    if ( this.table_leg_far )
      this.stretchLegTo( this.table_leg_far, this.leg_far_rest_height + dy );

    const at = this.top_body.translation();
    this.top_body.setTranslation( { x: at.x, y: this.top_body_rest_y + dy, z: at.z }, false );

    this.raiseLegColliders( dy );
  }

  private stretchLegTo( leg: THREE.Object3D, wanted_h: number ): void
  {
    const box = new THREE.Box3().setFromObject( leg );

    PhysicsTable.stretchLegPost( leg, wanted_h, box.max.y - box.min.y );
  }

  private raiseLegColliders( dy: number ): void
  {
    for ( const part of this.stretch_colliders )
    {
      part.collider.setHalfExtents( { x: part.half.x, y: part.half.y + dy * 0.5, z: part.half.z } );
      part.collider.setTranslationWrtParent( { x: part.at.x, y: part.at.y + dy * 0.5, z: part.at.z } );
    }
  }

  public isSliding(): boolean
  {
    return !!this.slide;
  }

  public getSlideDistance(): number
  {
    return this.slide?.distance ?? 0;
  }

  public getSlide(): number
  {
    return this.slide_offset;
  }

  public setSlide( dx: number ): void
  {
    if ( !this.slide )
      return;

    this.slide_offset = dx;

    this.tilt_pivot.position
      .copy( this.tilt_pivot_rest )
      .addScaledVector( this.slide_axis, dx * this.slide_units );
    this.model.updateMatrixWorld( true );

    const at = this.top_body.translation();
    this.top_body.setTranslation( { x: this.top_body_rest_x + dx, y: at.y, z: at.z }, false );
  }

  private captureSlideRest(): void
  {
    this.model.updateMatrixWorld( true );

    const inverse = PhysicsTable.tmp_slide_matrix.copy( this.model.matrixWorld ).invert();

    this.slide_axis.set( 1, 0, 0 ).transformDirection( inverse ).normalize();

    const scale = this.model.getWorldScale( PhysicsTable.tmp_scale );
    const world_per_local =
      Math.abs( this.slide_axis.x ) * scale.x +
      Math.abs( this.slide_axis.y ) * scale.y +
      Math.abs( this.slide_axis.z ) * scale.z;

    this.slide_units = 1 / ( world_per_local || 1 );

    this.tilt_pivot_rest.copy( this.tilt_pivot.position );
    this.top_body_rest_x = this.top_body.translation().x;
  }

  private captureElevatorRest(): void
  {
    this.model.updateMatrixWorld( true );

    this.tilt_pivot_rest_y = this.tilt_pivot.position.y;
    this.top_body_rest_y = this.top_body.translation().y;
    this.leg_rest_height = PhysicsTable.heightOf( this.table_leg );

    if ( this.table_leg_far )
      this.leg_far_rest_height = PhysicsTable.heightOf( this.table_leg_far );
  }

  private static heightOf( object: THREE.Object3D ): number
  {
    const box = new THREE.Box3().setFromObject( object );

    return box.max.y - box.min.y;
  }

  private applyTilt(): void
  {
    if ( this.tilt_rad === 0 && this.lift === 0 )
      return;

    if ( this.tilt_rad !== 0 )
    {
      const local_center = this.model.worldToLocal( this.untilted_top_center.clone() );
      this.tilt_pivot.position.copy( local_center );
      this.top_pivot.position.sub( local_center );

      this.tilt_pivot.rotation.x = this.tilt_rad;
      this.model.updateMatrixWorld( true );
    }

    const ground_y = this.stand_y ?? new THREE.Box3().setFromObject( this.table_leg ).min.y - this.lift;

    if ( this.tilt_rad === 0 )
    {
      this.fitLeg( this.table_leg, 0, ground_y );
      return;
    }

    const half_run = this.untilted_top_size.x * 0.5 * PhysicsTable.LEG_COVER_FRACTION;

    this.fitLeg( this.table_leg, -half_run, ground_y );
    this.table_leg_far = this.cloneLeg( this.table_leg );
    this.fitLeg( this.table_leg_far, half_run, ground_y );
  }

  private cloneLeg( source: THREE.Object3D ): THREE.Object3D
  {
    const clone = source.clone( true );
    clone.name = "table_leg_far";
    this.model.add( clone );
    return clone;
  }

  private fitLeg( leg: THREE.Object3D, world_dx: number, ground_y: number ): number
  {
    this.model.updateMatrixWorld( true );

    const local = this.model.worldToLocal(
      new THREE.Vector3( this.model.position.x + world_dx, this.model.position.y, this.model.position.z )
    );
    leg.position.z = local.z;

    this.model.updateMatrixWorld( true );

    const surface_y = this.untilted_top_center.y - Math.tan( this.tilt_rad ) * world_dx
      - this.untilted_top_size.y * 0.5;

    const leg_box = new THREE.Box3().setFromObject( leg );
    const current_h = leg_box.max.y - leg_box.min.y;
    const wanted_h = Math.max( surface_y - ground_y, current_h * 0.15 );

    PhysicsTable.stretchLegPost( leg, wanted_h, current_h );

    const world_scale_y = this.model.getWorldScale( PhysicsTable.tmp_scale ).y || 1;

    const fitted = new THREE.Box3().setFromObject( leg );
    leg.position.y += ( ground_y - fitted.min.y ) / world_scale_y;
    this.model.updateMatrixWorld( true );

    return wanted_h;
  }

  private leanLeg( leg: THREE.Object3D ): void
  {
    const angle = THREE.MathUtils.degToRad( PhysicsTable.LEG_LEAN_DEG );

    if ( angle === 0 || !leg.parent )
      return;

    this.model.updateMatrixWorld( true );

    const box = new THREE.Box3().setFromObject( leg );
    const height = box.max.y - box.min.y;
    const top = new THREE.Vector3( ( box.min.x + box.max.x ) * 0.5, box.max.y, ( box.min.z + box.max.z ) * 0.5 );

    PhysicsTable.stretchLegPost( leg, height / Math.cos( angle ), height );

    const drop = new THREE.Box3().setFromObject( leg ).max.y - top.y;
    const world = leg.getWorldPosition( new THREE.Vector3() );
    world.y -= drop;
    leg.position.copy( leg.parent.worldToLocal( world ) );
    leg.updateMatrixWorld( true );

    const parent_world = leg.parent.matrixWorld;
    const tip = new THREE.Matrix4()
      .makeTranslation( top.x, top.y, top.z )
      .multiply( new THREE.Matrix4().makeRotationX( angle ) )
      .multiply( new THREE.Matrix4().makeTranslation( -top.x, -top.y, -top.z ) );

    leg.applyMatrix4( parent_world.clone().invert().multiply( tip ).multiply( parent_world ) );
    leg.updateMatrixWorld( true );
  }

  private static stretchLegPost( leg: THREE.Object3D, wanted_h: number, current_h: number ): void
  {
    const post = PhysicsTable.findLegPost( leg );

    if ( !post || current_h <= 0 )
    {
      leg.scale.y *= wanted_h / current_h;
      leg.updateMatrixWorld( true );
      return;
    }

    const post_box = new THREE.Box3().setFromObject( post );
    const post_h = post_box.max.y - post_box.min.y;

    if ( post_h <= 0 || !post.parent )
      return;

    const foot_h = current_h - post_h;
    const wanted_post_h = Math.max( wanted_h - foot_h, PhysicsTable.MIN_LEG_POST_HEIGHT );
    const post_bottom = post_box.min.y;
    const up_axis = PhysicsTable.upAxisIndex( post );

    post.scale.setComponent( up_axis, post.scale.getComponent( up_axis ) * wanted_post_h / post_h );
    post.updateWorldMatrix( true, true );

    const drift = post_bottom - new THREE.Box3().setFromObject( post ).min.y;
    const world = post.getWorldPosition( new THREE.Vector3() );

    world.y += drift;
    post.position.copy( post.parent.worldToLocal( world ) );
    leg.updateMatrixWorld( true );
  }

  private static upAxisIndex( object: THREE.Object3D ): number
  {
    const e = object.matrixWorld.elements;
    let axis = 1;
    let best = -1;

    for ( let i = 0; i < 3; i++ )
    {
      const len = Math.hypot( e[ i * 4 ], e[ i * 4 + 1 ], e[ i * 4 + 2 ] );
      const upness = len > 0 ? Math.abs( e[ i * 4 + 1 ] ) / len : 0;

      if ( upness > best )
      {
        best = upness;
        axis = i;
      }
    }

    return axis;
  }

  private static findLegPost( node: THREE.Object3D ): THREE.Object3D | null
  {
    for ( const child of node.children )
    {
      if ( child.name.toLowerCase().includes( PhysicsTable.LEG_POST_TAG ) )
        return child;

      const found = PhysicsTable.findLegPost( child );

      if ( found )
        return found;
    }

    return null;
  }

  public getTiltRad(): number
  {
    return this.tilt_rad;
  }

  public surfaceYAt( x: number, _z: number ): number
  {
    if ( this.tilt_rad === 0 )
      return this.getTopY();

    const run = x - this.top_center.x;

    return this.untilted_top_center.y
      + this.untilted_top_size.y * 0.5 / Math.cos( this.tilt_rad )
      - Math.tan( this.tilt_rad ) * run;
  }

  public getTopSurfaceY(): number
  {
    return this.tilt_rad === 0
      ? this.getTopY()
      : this.surfaceYAt( this.top_center.x, this.top_center.z );
  }

  public getHighlightObject(): THREE.Object3D
  {
    return this.model;
  }

  private static prepareMap( texture_id: ResourceID ): THREE.Texture
  {
    const map = ResourceManager.getTexture( texture_id );
    map.minFilter = THREE.LinearMipmapLinearFilter;
    map.magFilter = THREE.LinearFilter;
    map.anisotropy = 4;
    map.generateMipmaps = true;
    map.needsUpdate = true;
    return map;
  }

  private static roundTopTexture( texture_id: ResourceID ): ResourceID
  {
    switch ( texture_id )
    {
      case ResourceID.TableGreyVioletBaseMap: return ResourceID.TableRoundGreyVioletBaseMap;
      case ResourceID.TableRedBaseMap:        return ResourceID.TableRoundRedBaseMap;
      default:                                return ResourceID.TableRoundBlueBaseMap;
    }
  }

  private createModel(
    position: THREE.Vector3,
    texture_id: ResourceID
  ): { model: THREE.Group; table_top: THREE.Object3D; table_leg: THREE.Object3D; top_pivot: THREE.Group; tilt_pivot: THREE.Group }
  {
    const model = new THREE.Group();
    const table_top = ResourceManager.getModel(
      this.rounded ? ResourceID.RoundedTableTopModel : ResourceID.TableTopModel
    );
    const table_leg = ResourceManager.getModel( ResourceID.TableLegModel );

    table_top.name = "table_top";
    table_leg.name = "table_leg";
    const top_pivot = new THREE.Group();
    top_pivot.add( table_top );

    const tilt_pivot = new THREE.Group();
    tilt_pivot.add( top_pivot );
    model.add( tilt_pivot, table_leg );

    // Shorten the support by one third without moving its foot. Lower the
    // tabletop by the same amount so both parts remain connected and the table
    // stays centred at its original world position.
    const leg_box_before = new THREE.Box3().setFromObject( table_leg );
    const leg_bottom = leg_box_before.min.y;

    PhysicsTable.shapeLeg( table_leg );
    table_leg.updateMatrixWorld( true );

    let leg_box_after = new THREE.Box3().setFromObject( table_leg );
    table_leg.position.y += leg_bottom - leg_box_after.min.y;
    table_leg.updateMatrixWorld( true );

    leg_box_after = new THREE.Box3().setFromObject( table_leg );

    // Resource clones share BufferGeometry. Each table must own its geometry
    // because its centre is sliced to a level-specific width and depth.
    table_top.traverse( obj =>
    {
      if ( obj instanceof THREE.Mesh )
        obj.geometry = obj.geometry.clone();
    } );

    // FBX centimetres arrive as a roughly 100-unit square. Normalize the only
    // authored tabletop before the common parent scale is applied.
    const current_box = new THREE.Box3().setFromObject( table_top );
    const current_size = current_box.getSize( new THREE.Vector3() );
    const target = PhysicsTable.getBaseTopSize().multiplyScalar( 2 );
    target.y *= 0.5;

    table_top.scale.multiply( new THREE.Vector3(
      target.x / current_size.x,
      target.y / current_size.y,
      target.z / current_size.z
    ) );
    table_top.updateMatrixWorld( true );

    // Keep a small overlap so no light seam appears between the tabletop and
    // its support.
    const normalized_box = new THREE.Box3().setFromObject( table_top );
    const overlap = 0.14;
    table_top.position.y += leg_box_after.max.y - overlap - normalized_box.min.y;

    // A level picks one shared tabletop colour across all of its tables.
    const table_map = PhysicsTable.prepareMap( texture_id );

    // The rounded top still uses the old UV layout, so it keeps the old
    // colour maps and the normal map authored for them.
    const top_map = this.rounded ? PhysicsTable.prepareMap( PhysicsTable.roundTopTexture( texture_id ) ) : table_map;
    const top_normal = this.rounded ? ResourceManager.getNormalMap( ResourceID.TableNormalMap ) : null;

    const material = new THREE.MeshLambertMaterial( {
      userData: { perf_tier: "large_static" },
      map: top_map,
      normalMap: top_normal,
      // Use uniform PBR values: the packed RM map contains dense surface
      // variation which reads as a woven pattern at gameplay distance.

      side: THREE.FrontSide
    } );

    // The support occupies far fewer pixels than the tabletop. A slightly
    // rougher dedicated material preserves its painted stripes and rounded
    // silhouette instead of letting one broad highlight blur them together.
    const leg_material = new THREE.MeshLambertMaterial( {
      userData: { perf_tier: "large_static" },
      map: table_map,

      side: THREE.FrontSide
    } );

    model.traverse( obj =>
    {
      if ( obj instanceof THREE.Mesh )
      {
        obj.castShadow = true;
        obj.receiveShadow = false;
        obj.material = material;
      }
    } );

    table_leg.traverse( obj =>
    {
      if ( obj instanceof THREE.Mesh )
        obj.material = leg_material;
    } );

    model.scale.setScalar( PhysicsTable.MODEL_SCALE );
    // The FBX files were authored with their long side along Z. The game table
    // uses X as its width, so rotate both parts together and preserve the
    // proportions/pivots supplied by the artist.
    model.rotation.y = PhysicsTable.MODEL_YAW;

    model.position.copy( position );
    model.position.y += this.lift;
    SceneManager.world.add( model );

    return { model, table_top, table_leg, top_pivot, tilt_pivot };
  }

  private resizeRoundTableTop( target_depth?: number, target_width?: number ): void
  {
    this.model.updateMatrixWorld( true );
    const top_size = new THREE.Box3().setFromObject( this.table_top ).getSize( new THREE.Vector3() );
    const leg_size = new THREE.Box3().setFromObject( this.table_leg ).getSize( new THREE.Vector3() );

    const wanted_x = Math.max( target_width ?? top_size.x, leg_size.x * PhysicsTable.LEG_COVER_FRACTION );
    const wanted_z = Math.max( target_depth ?? top_size.z, leg_size.z * PhysicsTable.LEG_COVER_FRACTION );

    let factor_x: number;
    let factor_z: number;

    if ( this.shape === "oval" )
    {
      factor_x = wanted_x / top_size.x;
      factor_z = wanted_z / top_size.z;
    }
    else
    {
      factor_x = Math.max( wanted_x, wanted_z ) / Math.max( top_size.x, top_size.z );
      factor_z = factor_x;
    }

    this.table_top.scale[ this.topScaleAxisFor( "x" ) ] *= factor_x;
    this.table_top.scale[ this.topScaleAxisFor( "z" ) ] *= factor_z;
    this.model.updateMatrixWorld( true );
  }

  private topScaleAxisFor( world_axis: "x" | "z" ): "x" | "y" | "z"
  {
    const world_dir = new THREE.Vector3( world_axis === "x" ? 1 : 0, 0, world_axis === "z" ? 1 : 0 );
    const local_dir = world_dir.transformDirection( this.table_top.matrixWorld.clone().invert() );
    const components = local_dir.toArray().map( Math.abs );
    const axes = [ "x", "y", "z" ] as const;

    return axes[ components.indexOf( Math.max( ...components ) ) ];
  }

  private stretchTableTopDepth( target_depth?: number ): void
  {
    if ( target_depth === undefined )
      return;

    this.model.updateMatrixWorld( true );
    const top_depth = new THREE.Box3().setFromObject( this.table_top )
      .getSize( new THREE.Vector3() ).z;
    const leg_depth = new THREE.Box3().setFromObject( this.table_leg )
      .getSize( new THREE.Vector3() ).z;

    const desired = Math.max( target_depth, leg_depth * PhysicsTable.LEG_COVER_FRACTION );

    this.stretchTableTopAlongWorldAxis( "z", desired - top_depth );
  }

  private stretchTableTopWidth( target_width?: number ): void
  {
    if ( target_width === undefined )
      return;

    this.model.updateMatrixWorld( true );
    const top_width = new THREE.Box3().setFromObject( this.table_top )
      .getSize( new THREE.Vector3() ).x;
    const leg_width = new THREE.Box3().setFromObject( this.table_leg )
      .getSize( new THREE.Vector3() ).x;

    const desired = Math.max( target_width, leg_width * PhysicsTable.LEG_COVER_FRACTION );

    this.stretchTableTopAlongWorldAxis( "x", desired - top_width );
  }

  private stretchTableTopAlongWorldAxis( world_axis: "x" | "z", extra: number ): void
  {
    if ( extra === 0 )
      return;

    this.model.updateMatrixWorld( true );

    const before = new THREE.Box3().setFromObject( this.table_top );
    const before_size = new THREE.Vector3();
    const before_center = new THREE.Vector3();
    before.getSize( before_size );
    before.getCenter( before_center );

    const base = before_size[world_axis];
    const factor = Math.max( ( base + extra ) / base, 0.1 );

    const world_dir = new THREE.Vector3(
      world_axis === "x" ? 1 : 0,
      0,
      world_axis === "z" ? 1 : 0
    );

    const local_dir = world_dir.clone()
      .transformDirection( this.table_top.matrixWorld.clone().invert() );

    const components = local_dir.toArray().map( Math.abs );
    const axes = [ "x", "y", "z" ] as const;
    const scale_axis = axes[ components.indexOf( Math.max( ...components ) ) ];

    centerSliceObjectGeometry( this.table_top, world_axis, factor );
    this.model.updateMatrixWorld( true );

    const after = new THREE.Box3().setFromObject( this.table_top );
    const after_center = new THREE.Vector3();
    after.getCenter( after_center );

    const parent = this.table_top.parent!;
    const shift = parent.worldToLocal(
        world_dir.clone().multiplyScalar( before_center[world_axis] - after_center[world_axis] )
      )
      .sub( parent.worldToLocal( new THREE.Vector3( 0, 0, 0 ) ) );
    this.table_top.position.add( shift );
    this.model.updateMatrixWorld( true );

    if ( !GlobalSettings.IS_PROD )
    {
      const other_axes = axes.filter( a => a !== world_axis );
      const final_box = new THREE.Box3().setFromObject( this.table_top );
      const final_size = new THREE.Vector3();
      const final_center = new THREE.Vector3();
      final_box.getSize( final_size );
      final_box.getCenter( final_center );

      if ( Math.abs( final_size[world_axis] / before_size[world_axis] - factor ) > 0.01
        || Math.abs( final_size[other_axes[0]] - before_size[other_axes[0]] ) > 1e-3
        || Math.abs( final_size[other_axes[1]] - before_size[other_axes[1]] ) > 1e-3
        || Math.abs( final_center[world_axis] - before_center[world_axis] ) > 1e-3 )
        console.warn( `[PhysicsTable] Stretch along ${world_axis} mis-scaled the tabletop: axis=${scale_axis}, factor=${factor.toFixed( 3 )}, size ${before_size.toArray().map( v => v.toFixed( 3 ) )} -> ${final_size.toArray().map( v => v.toFixed( 3 ) )}` );
    }
  }

  private calculateBounds(): void
  {
    this.box.setFromObject( this.model );
    this.box.getSize( this.size );
    this.box.getCenter( this.center );

    const top_box = new THREE.Box3().setFromObject( this.table_top );
    top_box.getCenter( this.top_center );
    top_box.getSize( this.top_size );

    if ( this.hang )
      this.leg_size.set( 0, 0, 0 );
    else
      new THREE.Box3().setFromObject( this.table_leg ).getSize( this.leg_size );
  }

  private createRigidBody(
    position: THREE.Vector3
  ): RAPIER.RigidBody
  {
    return PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation(
          position.x,
          position.y,
          position.z
        )
    );
  }

  private createTabletopBody(
    position: THREE.Vector3
  ): RAPIER.RigidBody
  {
    const origin = this.hang ? this.untilted_top_center : position;

    return PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(
          origin.x,
          origin.y,
          origin.z
        )
    );
  }

  private createTableTopCollider(
    position: THREE.Vector3
  ): void
  {
    const box = new THREE.Box3().setFromObject( this.table_top );

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    box.getSize( size );
    box.getCenter( center );
    this.platform_bottom_y = box.min.y;

    const tilted = this.tilt_rad !== 0;
    const half = ( tilted || this.hang )
      ? this.untilted_top_size.clone().multiplyScalar( 0.5 )
      : size.clone().multiplyScalar( 0.5 );
    const at = ( tilted || this.hang ) ? this.untilted_top_center : center;

    const local = this.hang
      ? new THREE.Vector3()
      : new THREE.Vector3( at.x - position.x, at.y - position.y, at.z - position.z );

    const parts = this.rounded
      ? PhysicsTable.curvedTopColliders( half )
      : [ { desc: RAPIER.ColliderDesc.cuboid( half.x, half.y, half.z ), offset: new THREE.Vector3() } ];

    const q = tilted
      ? new THREE.Quaternion().setFromAxisAngle( new THREE.Vector3( 0, 0, 1 ), -this.tilt_rad )
      : null;

    for ( const part of parts )
    {
      // An oval top is several parts side by side, so each one's own offset
      // has to turn with the tabletop, not just the tabletop's centre.
      const offset = q ? part.offset.applyQuaternion( q ) : part.offset;

      part.desc.setTranslation( local.x + offset.x, local.y + offset.y, local.z + offset.z );

      if ( q )
        part.desc.setRotation( { x: q.x, y: q.y, z: q.z, w: q.w } );

      PhysicsWorld.getWorld().createCollider( part.desc, this.top_body );
    }
  }

  private static curvedTopColliders( half: THREE.Vector3 ): { desc: RAPIER.ColliderDesc; offset: THREE.Vector3 }[]
  {
    if ( Math.abs( half.x - half.z ) < 1e-4 )
      return [ {
        desc: RAPIER.ColliderDesc.cylinder( half.y, Math.min( half.x, half.z ) ),
        offset: new THREE.Vector3(),
      } ];

    const along_x = half.x >= half.z;
    const long = along_x ? half.x : half.z;
    const short = along_x ? half.z : half.x;
    const bands = PhysicsTable.OVAL_COLLIDER_BANDS;
    const step = ( long * 2 ) / bands;
    const parts: { desc: RAPIER.ColliderDesc; offset: THREE.Vector3 }[] = [];

    for ( let i = 0; i < bands; i++ )
    {
      const centre = -long + ( i + 0.5 ) * step;
      const t = Math.min( Math.abs( centre ) / long, 1 );
      const half_short = short * Math.sqrt( Math.max( 1 - t * t, 0 ) );

      if ( half_short <= 1e-4 )
        continue;

      parts.push( {
        desc: along_x
          ? RAPIER.ColliderDesc.cuboid( step * 0.5, half.y, half_short )
          : RAPIER.ColliderDesc.cuboid( half_short, half.y, step * 0.5 ),
        offset: along_x
          ? new THREE.Vector3( centre, 0, 0 )
          : new THREE.Vector3( 0, 0, centre ),
      } );
    }

    return parts;
  }

  private createLegCollider( position: THREE.Vector3 ): void
  {
    this.addLegCollider( this.table_leg, position );

    if ( this.table_leg_far )
      this.addLegCollider( this.table_leg_far, position );
  }

  private addLegCollider( table_leg: THREE.Object3D, position: THREE.Vector3 ): void
  {
    const box = new THREE.Box3().setFromObject( table_leg );

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    box.getSize( size );
    box.getCenter( center );

    const bottom_h = size.y * 0.3;
    const top_h = size.y - bottom_h;

    const half = new THREE.Vector3( size.x * 0.15, top_h * 0.5, size.z * 0.25 );
    const at = new THREE.Vector3(
      center.x - position.x,
      box.max.y - top_h * 0.5 - position.y,
      center.z - position.z
    );

    const post_collider = PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( half.x, half.y, half.z )
        .setTranslation( at.x, at.y, at.z ),
      this.body
    );

    if ( this.elevator )
      this.stretch_colliders.push( { collider: post_collider, half, at } );

    PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid(
        size.x * 0.4,
        bottom_h * 0.35,
        size.z * 0.5
      ).setTranslation(
        center.x - position.x,
        box.min.y + bottom_h * 0.35 - position.y,
        center.z - position.z
      ),
      this.body
    );
  }

  public setFieldAngle( rad: number ): void
  {
    this.rotation_angle = rad;

    // setRotation (not setNextKinematicRotation): the top must have zero
    // surface velocity, otherwise contact friction drags resting blocks that
    // the FieldRotator already carries, shearing towers apart.
    this.top_body.setRotation(
      {
        x: 0,
        y: Math.sin( rad * 0.5 ),
        z: 0,
        w: Math.cos( rad * 0.5 )
      },
      false
    );

    this.top_pivot.rotation.y = rad;
  }

  public getFieldAngle(): number
  {
    return this.rotation_angle;
  }

  public getPivot(): THREE.Vector3
  {
    return this.pivot;
  }

  public getBodyHandles(): number[]
  {
    return [ this.body.handle, this.top_body.handle ];
  }

  public getTopBodyHandle(): number
  {
    return this.top_body.handle;
  }

  // Reflects the table across the plane x = axis_x, bodies and cached bounds
  // alike. For flat tables only: a tilt or a hang rig would have to flip too.
  public mirrorX( axis_x: number ): void
  {
    const flip = ( x: number ): number => 2 * axis_x - x;

    this.pivot.x = flip( this.pivot.x );
    this.center.x = flip( this.center.x );
    this.top_center.x = flip( this.top_center.x );
    this.untilted_top_center.x = flip( this.untilted_top_center.x );

    const box_min_x = flip( this.box.max.x );
    this.box.max.x = flip( this.box.min.x );
    this.box.min.x = box_min_x;

    for ( const body of [ this.body, this.top_body ] )
    {
      const at = body.translation();

      body.setTranslation( { x: flip( at.x ), y: at.y, z: at.z }, false );

      for ( let i = 0; i < body.numColliders(); i++ )
      {
        const collider = body.collider( i );
        const local = collider.translationWrtParent();

        if ( local )
          collider.setTranslationWrtParent( { x: -local.x, y: local.y, z: local.z } );
      }
    }

    if ( this.surface_rest )
      this.surface_rest.x = flip( this.surface_rest.x );
  }

  public setModelVisible( visible: boolean ): void
  {
    this.model.visible = visible;
  }

  public setLegSolid( solid: boolean ): void
  {
    for ( let i = 0; i < this.body.numColliders(); i++ )
      this.body.collider( i ).setEnabled( solid );
  }

  public driveSurface( velocity: THREE.Vector3, dt: number ): void
  {
    const rest = this.surface_rest ??= new THREE.Vector3().copy( this.top_body.translation() );

    this.top_body.setTranslation( rest, false );
    this.top_body.setNextKinematicTranslation( {
      x: rest.x + velocity.x * dt,
      y: rest.y + velocity.y * dt,
      z: rest.z + velocity.z * dt,
    } );
  }

  public toUnrotatedFrame( pos: RAPIER.Vector, out: THREE.Vector3 ): THREE.Vector3
  {
    const at = this.unswing( pos );
    const c = Math.cos( -this.rotation_angle );
    const s = Math.sin( -this.rotation_angle );
    const dx = at.x - this.pivot.x;
    const dz = at.z - this.pivot.z;

    // The slide is undone last, in world X: the deck only ever travels along
    // that axis, and a sliding table is never also spun (GameScreen refuses the
    // pair), so there is no rotated frame to undo it in.
    return out.set(
      this.pivot.x + dx * c + dz * s - this.slide_offset,
      at.y - this.elevation,
      this.pivot.z - dx * s + dz * c
    );
  }

  private unswing( pos: RAPIER.Vector ): RAPIER.Vector
  {
    if ( this.swing_angle === 0 || !this.hang_pivot_world )
      return pos;

    const q = PhysicsTable.tmp_unswing_quat
      .setFromAxisAngle( PhysicsTable.HANG_AXIS, this.swing_angle );

    return PhysicsTable.tmp_unswing
      .set( pos.x, pos.y, pos.z )
      .sub( this.hang_pivot_world )
      .applyQuaternion( q )
      .add( this.hang_pivot_world );
  }

  // Only the space above the tabletop counts as the rotating field. A block
  // knocked below the top surface (even inside the table's XZ footprint) must
  // be released so it falls straight instead of orbiting along the rim.
  public isOutside( pos: RAPIER.Vector ): boolean
  {
    const local = this.toUnrotatedFrame( pos, this.unrotated_pos );

    const surface_y = this.tilt_rad === 0 ? this.box.max.y : this.surfaceYAt( local.x, local.z );

    if ( this.rounded )
    {
      const dx = local.x - this.top_center.x;
      const dz = local.z - this.top_center.z;
      const rx = this.top_size.x * 0.5;
      const rz = this.top_size.z * 0.5;

      return local.y < surface_y - 0.1
        || ( dx * dx ) / ( rx * rx ) + ( dz * dz ) / ( rz * rz ) > 1;
    }

    return (
      local.y < surface_y - 0.1 ||
      local.x < this.top_center.x - this.top_size.x / 2 ||
      local.x > this.top_center.x + this.top_size.x / 2 ||
      local.z < this.top_center.z - this.top_size.z / 2 ||
      local.z > this.top_center.z + this.top_size.z / 2
    );
  }

  public ownsCollider( collider: RAPIER.Collider ): boolean
  {
    const parent = collider.parent()?.handle;

    return parent === this.top_body.handle || parent === this.body.handle;
  }

  public getHeight(): number
  {
    return this.size.y;
  }

  public getTopY(): number
  {
    return this.box.max.y;
  }

  public isBelowSurface( pos: RAPIER.Vector ): boolean
  {
    if ( this.tilt_rad === 0 )
      return pos.y < this.getTopY();

    const local = this.toUnrotatedFrame( pos, this.unrotated_pos );

    return local.y < this.surfaceUnder( local );
  }

  public heightAboveSurface( pos: RAPIER.Vector ): number
  {
    const local = this.toUnrotatedFrame( pos, this.unrotated_pos );

    return local.y - ( this.tilt_rad === 0 ? this.getTopY() : this.surfaceUnder( local ) );
  }

  private surfaceUnder( local: THREE.Vector3 ): number
  {
    const half_x = this.top_size.x * 0.5;
    const x = THREE.MathUtils.clamp( local.x, this.top_center.x - half_x, this.top_center.x + half_x );

    return this.surfaceYAt( x, local.z );
  }

  public getPlatformBottomY(): number
  {
    return this.platform_bottom_y;
  }

  public getCenter(): THREE.Vector3
  {
    return this.center.clone();
  }

  public getTopCenter(): THREE.Vector3
  {
    return this.top_center.clone();
  }

  public getTopSize(): THREE.Vector3
  {
    return this.top_size.clone();
  }

  public getLegSize(): THREE.Vector3
  {
    return this.leg_size.clone();
  }

  public getSize(): THREE.Vector3
  {
    return this.size.clone();
  }

  public dispose(): void
  {
    SceneManager.world.remove( this.model );

    PhysicsWorld.getWorld().removeRigidBody( this.body );
    PhysicsWorld.getWorld().removeRigidBody( this.top_body );
  }
}
